import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Spacing, Text, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { useSelector } from 'react-redux';
import usePurchase from 'hooks/store/usePurchase';
import getCompletedCdsPointsUsed from 'utils/purchase/getCompletedCdsPointsUsed';
import { trackEvent } from 'user-analytics';
import PurchasePricingPassengerTrip from 'components/purchase/PurchasePricingPassengerTrip';
import PurchasePricingTrip from 'components/purchase/PurchasePricingTrip';
import PurchasePricingExtras from 'components/purchase/PurchasePricingExtras';
import PurchasePricingDiscount from 'components/purchase/PurchasePricingDiscount';
import PurchasePricingPreviousPurchase from 'components/purchase/PurchasePricingPreviousPurchase';
import TransferNotice from 'components/TransferNotice';
import usePricingBeforeCheckout from 'hooks/usePricingBeforeCheckout';
import usePricingTotal from 'hooks/usePricingTotal';
import useShowPricesByGroup from 'hooks/useShowPricesByGroup';
import { useTranslation } from 'react-i18next';
import PurchasePricingBulk from 'components/purchase/PurchasePricingBulk';
import { purchaseTotalByCategory } from 'utils/Reserbus';
import { purchaseHasFreeFarePassenger } from 'utils/freeFareDocumentRules';
import {
  getApplicableLegPricing,
  isBfoSupportedJourney,
  resolveBfoEffectiveSeatPrice,
} from 'utils/purchase/baseFareOverride';
import CollapsibleTriggerChevronText from 'ui/atoms/CollapsibleTriggerChevronText';
import { PRICING_DETAILS_DROPDOWN_OPENED } from 'constants/TrackEvents';
import CdsPointsPricingRow from '../CdsPointsCheckout/CdsPointsPricingRow';
import CdsPointsPricingDetails from '../CdsPointsCheckout/CdsPointsPricingDetails';
import getPurchaseStepName from '../../../utils/purchase/getPurchaseStepName';
import 'styles/components/purchase/PurchasePricing';
import PurchaseFares from '../PurchaserForm/PurchaseFares';

const propTypes = {
  departs: PropTypes.object.isRequired,
  extras: PropTypes.shape({
    carbon: PropTypes.number,
    ancillaries: PropTypes.number,
    total: PropTypes.number.isRequired,
    installmentsFee: PropTypes.number.isRequired,
    insurance: PropTypes.number.isRequired,
    markup: PropTypes.number.isRequired,
    fixedFee: PropTypes.shape({
      amount: PropTypes.number,
      tax: PropTypes.number,
    }),
  }).isRequired,
  hasMixedTrips: PropTypes.bool.isRequired,
  passengers: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  passengerCount: PropTypes.number.isRequired,
  titleFooterDescription: PropTypes.string.isRequired,
  titleHeaderDescription: PropTypes.string.isRequired,
  titleTotal: PropTypes.number.isRequired,
  returns: PropTypes.object.isRequired,
  roundTrip: PropTypes.bool.isRequired,
  externalCouponCode: PropTypes.string,
  selectedInstallmentsPlan: PropTypes.string.isRequired,
  monthlySelectedPlan: PropTypes.string.isRequired,
  exchangedTotal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedPaymentOption: PropTypes.shape({
    months: PropTypes.number.isRequired,
  }),
  departsFares: PropTypes.arrayOf(PropTypes.object),
  returnsFares: PropTypes.arrayOf(PropTypes.object),
  isExchange: PropTypes.bool,
  exchangeSurcharges: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        message: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    total: PropTypes.number,
  }),
  isBulkTicket: PropTypes.bool,
  overbooking: PropTypes.bool,
  hasCollapsibleTrigger: PropTypes.bool,
};

/**
 * Calculate purchase pricing based on trips and passengers data
 * @param {Object} departs - Departure trip information including selected seats and prices
 * @param {Object} returns - Return trip information including selected seats and prices
 * @param {Array} passengers - Array of passenger information
 * @param {boolean} useBaseFareOverride - Whether supported canonical BFO quotes may apply.
 * @returns {Object} - Calculated pricing data including totals and seat-based pricing
 */
export const getPurchasePricing = (departs, returns, passengers, useBaseFareOverride = false) => {
  const isRoundTrip = Boolean(returns?.fragments?.length);
  const getWaySelectedSeatsTotal = (reservation, way) => {
    const pricing = getApplicableLegPricing(reservation?.fragments?.[0], { way, isRoundTrip });
    return (reservation?.selectedSeats ?? []).reduce((sum, seat) => {
      const price = useBaseFareOverride
        ? resolveBfoEffectiveSeatPrice({ pricing, seat })
        : seat.price;
      return sum + (Number(price) || 0);
    }, 0);
  };

  // Calculate total prices from selected seats
  const departsSelectedSeatsTotal = getWaySelectedSeatsTotal(departs, 'departs');
  const returnsSelectedSeatsTotal = getWaySelectedSeatsTotal(returns, 'returns');
  const selectedSeatsTotal = departsSelectedSeatsTotal + returnsSelectedSeatsTotal;

  const departsPrices = departs.fragments[0].prices;
  const {
    total: departBulkTotal,
    taxes: departBulkTaxes,
    totalsPerCategory: departsTotalsPerCategory,
  } = purchaseTotalByCategory(passengers, departsPrices);
  if (!returns?.fragments?.length) {
    return {
      globalBulkTotal: departBulkTotal,
      globalBulkTaxes: departBulkTaxes,
      totalsPerCategory: departsTotalsPerCategory,
      departsTotalsPerCategory,
      returnsTotalsPerCategory: undefined,
      departsSelectedSeatsTotal,
      returnsSelectedSeatsTotal,
      selectedSeatsTotal,
    };
  }
  const returnsPrices = returns.fragments[0].prices;

  const {
    total: returnBulkTotal,
    taxes: returnBulkTaxes,
    totalsPerCategory: returnsTotalsPerCategory,
  } = purchaseTotalByCategory(passengers, returnsPrices);

  const globalBulkTotal = departBulkTotal + returnBulkTotal;
  const globalBulkTaxes = departBulkTaxes + returnBulkTaxes;
  const totalsPerCategory = [...departsTotalsPerCategory, ...returnsTotalsPerCategory];

  return {
    globalBulkTotal,
    globalBulkTaxes,
    totalsPerCategory,
    departsTotalsPerCategory,
    returnsTotalsPerCategory,
    departsSelectedSeatsTotal,
    returnsSelectedSeatsTotal,
    selectedSeatsTotal,
  };
};

/**
 * PurchasePricing component.
 * Render the pricing breakdown for a purchase including trips, extras, and discounts.
 * @param {object} props - departs, extras, hasMixedTrips, passengers, passengerCount, titleFooterDescription, titleHeaderDescription, titleTotal, returns, roundTrip, externalCouponCode, selectedInstallmentsPlan, monthlySelectedPlan, selectedPaymentOption, departsFares, returnsFares, isExchange, exchangedTotal, exchangeSurcharges, isBulkTicket, overbooking, hasCollapsibleTrigger.
 * @param {object} props.departs - Departure trip information including selected seats and prices.
 * @param {object} props.extras - Extra charges including carbon, ancillaries, total, installmentsFee, insurance, and markup.
 * @param {boolean} props.hasMixedTrips - Whether the purchase contains mixed trip types.
 * @param {Array} props.passengers - Array of passenger information objects.
 * @param {number} props.passengerCount - Total number of passengers.
 * @param {string} props.titleFooterDescription - Description text for the footer.
 * @param {string} props.titleHeaderDescription - Description text for the header.
 * @param {number} props.titleTotal - Total price for the purchase.
 * @param {object} props.returns - Return trip information including selected seats and prices.
 * @param {boolean} props.roundTrip - Whether this is a round trip purchase.
 * @param {string} props.externalCouponCode - External coupon code if applicable.
 * @param {string} props.selectedInstallmentsPlan - Selected installments plan identifier.
 * @param {string} props.monthlySelectedPlan - Selected monthly payment plan.
 * @param {object} props.selectedPaymentOption - Payment option with months property.
 * @param {Array} props.departsFares - Array of departure fare objects.
 * @param {Array} props.returnsFares - Array of return fare objects.
 * @param {boolean} props.isExchange - Whether this is an exchange transaction.
 * @param {number|string} props.exchangedTotal - Total amount for exchange.
 * @param {number} [props.exchangeSurcharges] - Surcharge amount applied in exchange flows.
 * @param {boolean} props.isBulkTicket - Whether this is a bulk ticket purchase.
 * @param {boolean} props.overbooking - Whether the trip has overbooking.
 * @param {boolean} props.hasCollapsibleTrigger - Whether to show collapsed passenger view.
 * @returns {JSX.Element|null} The pricing component or null if conditions are not met.
 */
const PurchasePricing = ({
  departs,
  extras,
  hasMixedTrips,
  passengers,
  passengerCount,
  titleFooterDescription,
  titleHeaderDescription,
  titleTotal,
  returns,
  roundTrip,
  externalCouponCode,
  selectedInstallmentsPlan,
  monthlySelectedPlan,
  selectedPaymentOption,
  departsFares,
  returnsFares,
  isExchange,
  exchangedTotal,
  exchangeSurcharges,
  isBulkTicket,
  overbooking,
  hasCollapsibleTrigger = true,
}) => {
  const purchaseJs = usePurchase();
  const hasFreeFarePassenger = purchaseHasFreeFarePassenger(purchaseJs);
  const [isOpen, setIsOpen] = useState(hasFreeFarePassenger);
  const freeFareAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (hasFreeFarePassenger && !freeFareAutoOpenedRef.current) {
      freeFareAutoOpenedRef.current = true;
      setIsOpen(true);
    }
  }, [hasFreeFarePassenger]);

  /**
   * Handle collapsible toggle
   */
  const handleCollapsibleToggle = () => {
    setIsOpen((prev) => {
      // Track event only when opening the dropdown (prev is false)
      if (!prev) {
        trackEvent(PRICING_DETAILS_DROPDOWN_OPENED);
      }
      return !prev;
    });
  };

  const {
    passengersCount: passengerCountBeforeCheckout,
    purchaseTotal: purchaseTotalBeforeCheckout,
    departTripPricing: departTripPricingBeforeCheckout,
    returnTripPricing: returnTripPricingBeforeCheckout,
    selectedSeats,
    displayPricingBeforeCheckout,
    fixedFeeTotals,
  } = usePricingBeforeCheckout({ isRoundTrip: roundTrip });

  const { features, env } = useSelector((state) => state.whitelabelConfig);
  const { SHOW_NON_EXACT_PRICE_MESSAGE } = features;
  const useSeatBasedPricing = useShowPricesByGroup();
  const useBaseFareOverride = isBfoSupportedJourney({
    purchase: purchaseJs,
    features,
    brand: env.brand,
  });
  const location = useLocation();
  const { t } = useTranslation('purchase');
  const { t: tExchange } = useTranslation('exchange');
  const purchaseStep = getPurchaseStepName(location.pathname);
  const cdsPointsSelected = purchaseStep === 'checkout' && purchaseJs.cdsPointsPreview != null;
  const consumedPoints = purchaseStep === 'complete' ? getCompletedCdsPointsUsed(purchaseJs) : null;

  // Determine if seat-based pricing should be used
  const useSelectedSeats = useSeatBasedPricing && !departs.tickets?.length;

  const {
    globalBulkTotal,
    globalBulkTaxes,
    departsTotalsPerCategory,
    returnsTotalsPerCategory,
    departsSelectedSeatsTotal,
    returnsSelectedSeatsTotal,
    selectedSeatsTotal,
  } = getPurchasePricing(departs, returns, passengers, useBaseFareOverride);

  // Fixed platform-usage fee (MID-747). Before checkout the purchase does not
  // have a ticket per selected seat yet, so the purchase-level totals in the
  // store lag behind the seat selection. While the pre-checkout pricing drives
  // the displayed total, the fee comes from the same source that total comes
  // from so the breakdown adds up and matches the seats tooltip. `null` there
  // means "nothing to disclose": falling back to the purchase-level totals
  // would keep the fee rows on screen after deselecting every seat.
  const pricingExtras = displayPricingBeforeCheckout
    ? { ...extras, fixedFee: fixedFeeTotals }
    : extras;

  // Calculate total price based on purchase configuration
  const calculatedTotal = usePricingTotal({
    useSelectedSeats,
    selectedSeatsTotal,
    isBulkTicket,
    monthlySelectedPlan,
    selectedInstallmentsPlan,
    globalBulkTotal,
    displayPricingBeforeCheckout,
    purchaseTotalBeforeCheckout,
    total: titleTotal,
  });

  const total = consumedPoints !== null ? purchaseJs.total : calculatedTotal;

  if (
    !['checkout', 'complete', 'quickCheckout'].includes(purchaseStep) &&
    !displayPricingBeforeCheckout
  )
    return null;

  let detailsTitleHeader = '';

  if (selectedPaymentOption === 'credit_card' && monthlySelectedPlan > 1) {
    detailsTitleHeader = t('label.instalments_title', {
      number: selectedInstallmentsPlan.months,
    });
  } else {
    detailsTitleHeader = t(`label.${titleHeaderDescription}`);
  }

  const exchangeTripTextBrand = {
    gfa: 'your_trip_gfa',
    default: 'your_trip',
  };

  const simpleTextBrand = {
    gfa: 'your_trip_web_gfa',
    default: 'your_trip',
  };

  const simpleTripPriceText = isExchange
    ? exchangeTripTextBrand[env.brand] || exchangeTripTextBrand.default
    : simpleTextBrand[env.brand] || simpleTextBrand.default;
  const departsTotalLabel = roundTrip ? 'your_one_way_trip' : simpleTripPriceText;
  const returnsTotalLabel = 'your_return_trip';

  // Show price disclaimer only in exchange flow when prices may not be exact and before seats are locked
  const shouldShowExchangePriceDisclaimer =
    isExchange && SHOW_NON_EXACT_PRICE_MESSAGE && !['checkout', 'complete'].includes(purchaseStep);

  return (
    <div>
      {hasMixedTrips ? <TransferNotice /> : null}
      {shouldShowExchangePriceDisclaimer && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-4">
          <div className="flex items-center gap-3">
            <Icon type="Info" size="M" color="primary" className="flex-shrink-0" />
            <Text size="S" color="grayDark">
              {tExchange('info.price_disclaimer')}
            </Text>
          </div>
        </div>
      )}
      {isBulkTicket ? (
        <div>
          <PurchasePricingBulk
            labelBulkType={t('label.your_one_way_trip')}
            totalsPerCategory={departsTotalsPerCategory}
          />
          {returnsTotalsPerCategory && (
            <PurchasePricingBulk
              labelBulkType={t('label.your_return_trip')}
              totalsPerCategory={returnsTotalsPerCategory}
            />
          )}
          <PurchasePricingBulk
            globalTaxes={globalBulkTaxes}
            globalTotal={globalBulkTotal}
            labelTaxes={t('extra_price')}
            labelTotal={t('label.total')}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-y-3">
          {features.TRIP_PRICING_BY_PASSENGER ? (
            <>
              <PurchasePricingPassengerTrip
                segment="outgoing"
                reservation={departs}
                way="departs"
                passengers={passengers}
                passengerCount={passengerCount}
                titleLabel={t(roundTrip ? 'label.departure_detail' : 'label.passengers')}
                currency={env.currency}
                externalCouponCode={externalCouponCode}
                selectedSeatsBeforeCheckout={selectedSeats.departs}
                displayPricingBeforeCheckout={displayPricingBeforeCheckout}
                selectedSeatsTotal={departsSelectedSeatsTotal}
                overbooking={overbooking}
                roundTrip={roundTrip}
                isOpen={isOpen}
                hasCollapsibleTrigger={!hasCollapsibleTrigger}
              />
            </>
          ) : (
            <PurchasePricingTrip
              reservation={departs}
              passengers={passengers}
              passengerCount={passengerCount}
              totalLabel={departsTotalLabel}
              displayPricingBeforeCheckout={displayPricingBeforeCheckout}
              passengersCountBeforeCheckout={passengerCountBeforeCheckout}
              pricingBeforeCheckout={departTripPricingBeforeCheckout}
              exchangedTotal={exchangedTotal}
              isOpen={isOpen}
              hasCollapsibleTrigger={!hasCollapsibleTrigger}
            />
          )}

          {roundTrip ? (
            <>
              <div className="border border-color-gray-200/60 border-dashed" />

              {features.TRIP_PRICING_BY_PASSENGER ? (
                <PurchasePricingPassengerTrip
                  segment="incoming"
                  reservation={returns}
                  way="returns"
                  passengers={passengers}
                  passengerCount={passengerCount}
                  titleLabel={t('label.return_detail')}
                  currency={env.currency}
                  externalCouponCode={externalCouponCode}
                  selectedSeatsBeforeCheckout={selectedSeats.returns}
                  displayPricingBeforeCheckout={displayPricingBeforeCheckout}
                  selectedSeatsTotal={returnsSelectedSeatsTotal}
                  overbooking={overbooking}
                  roundTrip={roundTrip}
                  isOpen={isOpen}
                  hasCollapsibleTrigger={!hasCollapsibleTrigger}
                />
              ) : (
                <PurchasePricingTrip
                  reservation={returns}
                  passengers={passengers}
                  passengerCount={passengerCount}
                  totalLabel={returnsTotalLabel}
                  displayPricingBeforeCheckout={displayPricingBeforeCheckout}
                  passengersCountBeforeCheckout={passengerCountBeforeCheckout}
                  pricingBeforeCheckout={returnTripPricingBeforeCheckout}
                  isOpen={isOpen}
                  hasCollapsibleTrigger={!hasCollapsibleTrigger}
                />
              )}
            </>
          ) : null}
          <div className="border border-color-gray-200/60 border-dashed" />
          <Spacing vertical size="XS">
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <PurchasePricingExtras {...pricingExtras} exchangeSurcharges={exchangeSurcharges} />
            <PurchasePricingDiscount />
            <PurchasePricingPreviousPurchase exchangedTotal={exchangedTotal} />
            {features.SELECTABLE_FARE && (
              <PurchaseFares departsFares={departsFares} returnsFares={returnsFares} />
            )}
          </Spacing>

          {cdsPointsSelected && <CdsPointsPricingRow amount={purchaseJs.cdsPointsPreview} />}
          {purchaseStep === 'complete' && (
            <CdsPointsPricingDetails
              consumedPoints={consumedPoints}
              hasAdditionalServices={pricingExtras.total > 0}
            />
          )}
          <Spacing size="S" alignItems="center" justifyContent="space-between">
            <Spacing size="XS" vertical>
              <Text>{detailsTitleHeader}</Text>
              {titleFooterDescription && (
                <Text size="S" italic>
                  {titleFooterDescription}
                </Text>
              )}
            </Spacing>
            <LocalizedCurrency
              color="accent"
              currencyStyle="currency"
              price={cdsPointsSelected ? 0 : total}
              showCurrency
              size="L"
              weight="bold"
            />
          </Spacing>

          {hasCollapsibleTrigger && (
            <CollapsibleTriggerChevronText
              isOpen={isOpen}
              onClick={handleCollapsibleToggle}
              hideText={t('label.hide_payment_details')}
              expandText={t('label.expand_payment_details')}
            />
          )}
        </div>
      )}
    </div>
  );
};

PurchasePricing.propTypes = propTypes;

export default PurchasePricing;
